import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheoryProgress } from '../../hooks/useTheoryProgress';
import { useVideoProgress } from '../../hooks/useVideoProgress';

const ControlFlow: React.FC = () => {
  const navigate = useNavigate();
  useTheoryProgress('controlFlow');
  const videoRef = useVideoProgress('controlFlow');

  const handleBackClick = () => {
    navigate('/topics');
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Minimal grid background */}
      <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:64px_64px] [background-position-x:center]"></div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">
        {/* Header with back button */}
        <div className="flex items-center justify-between mb-8">
          <motion.button
            onClick={handleBackClick}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-sm font-medium">Back to Topics</span>
          </motion.button>
        </div>

        {/* Title and description */}
        <motion.div 
          className="mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-normal text-white mb-2">Control Flow</h1>
          <p className="text-white/70 text-lg">Master the art of directing program execution through conditions, loops, and error handling.</p>
        </motion.div>

        <div className="space-y-12">
          {/* Introduction */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">What is Control Flow?</h2>
            <div className="text-white/70 space-y-4">
              <p>
                Control flow determines the order in which a program's statements are executed. It's the backbone of programming logic, 
                allowing developers to create dynamic, responsive applications by controlling execution paths.
              </p>
              <p>
                Imagine control flow as a traffic system for your code: conditions act as traffic lights, loops as roundabouts, 
                and error handling as emergency response protocols. Together, they ensure your program runs smoothly and predictably.
              </p>
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mt-6">
                <h3 className="text-xl font-semibold text-white mb-3">Core Principles</h3>
                <ul className="list-disc list-inside text-white/70 space-y-2">
                  <li><strong>Sequential Execution:</strong> Default line-by-line execution</li>
                  <li><strong>Conditional Branching:</strong> Decision-based path selection</li>
                  <li><strong>Iteration:</strong> Repeated execution of code blocks</li>
                  <li><strong>Exception Management:</strong> Handling unexpected events</li>
                </ul>
              </div>
            </div>
          </motion.section>

          {/* Core Control Structures */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Core Control Structures</h2>
            <div className="space-y-6">
            {/* Conditional Statements */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">Conditional Statements</h3>
                  <div className="text-2xl">🤔</div>
                </div>
                <p className="text-white/70 mb-4">
                  Conditional statements enable decision-making by executing different code blocks based on boolean conditions.
              </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`// If-else statement
const age = 20;
if (age >= 18) {
  console.log('Adult');
} else if (age >= 13) {
  console.log('Teen');
} else {
  console.log('Child');
}

// Switch statement
const day = 'Monday';
switch (day) {
  case 'Monday':
    console.log('Start of week');
    break;
  case 'Friday':
    console.log('End of week');
    break;
  default:
    console.log('Midweek');
}

// Ternary operator
const status = age >= 18 ? 'Adult' : 'Minor';`}</code>
                  </pre>
              </div>
            </div>

            {/* Loops */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Loops</h3>
                  <div className="text-2xl">🔄</div>
                </div>
                <p className="text-white/70 mb-4">
                  Loops allow repetitive execution of code until a condition is met or for a specified number of iterations.
              </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`// For loop
for (let i = 0; i < 5; i++) {
  console.log(\`Count: \${i}\`);
}

// While loop
let count = 0;
while (count < 5) {
  console.log(\`Count: \${count}\`);
  count++;
}

// For...of (arrays)
const numbers = [1, 2, 3];
for (const num of numbers) {
  console.log(num);
}

// For...in (objects)
const person = { name: 'John', age: 30 };
for (const key in person) {
  console.log(\`\${key}: \${person[key]}\`);
}`}</code>
                  </pre>
              </div>
            </div>

            {/* Error Handling */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">Error Handling</h3>
                  <div className="text-2xl">🚨</div>
                </div>
                <p className="text-white/70 mb-4">
                  Error handling manages runtime exceptions to prevent program crashes and ensure graceful recovery.
              </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`// Custom Error
class CustomError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CustomError';
  }
}

// Try-catch with finally
try {
  const data = JSON.parse('invalid json');
} catch (error) {
  if (error instanceof SyntaxError) {
    console.log('Parsing failed');
  } else {
    console.log('Unknown error:', error);
  }
} finally {
  console.log('Cleanup complete');
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Async Flow */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Asynchronous Flow</h3>
                  <div className="text-2xl">⏳</div>
            </div>
                <p className="text-white/70 mb-4">
                  Asynchronous control flow manages operations that take time, like API calls or file operations.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`// Promise
const fetchData = new Promise((resolve, reject) => {
  setTimeout(() => resolve('Data'), 1000);
});

// Async/await
async function getData() {
  try {
    const response = await fetch('https://api.example.com');
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Fetch failed:', error);
  }
}

// Generator
function* numberGenerator() {
  yield 1;
  yield 2;
  yield 3;
}
const gen = numberGenerator();
console.log(gen.next().value); // 1`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Control Flow Patterns */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Control Flow Patterns</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                Advanced patterns enhance control flow for complex scenarios:
              </p>
              <ul className="list-disc list-inside text-white/70 space-y-4">
                <li>
                  <strong>Short-circuit Evaluation:</strong> Using logical operators to skip unnecessary evaluations.
                  <div className="bg-white/5 p-4 rounded-lg mt-2">
                    <pre className="text-sm text-white/80 font-mono">
                      <code>{`const isValid = user && user.active;
if (isValid || checkPermissions()) {
  // Only evaluates checkPermissions() if isValid is false
}`}</code>
                    </pre>
                  </div>
                </li>
                <li>
                  <strong>Loop Control:</strong> Using break and continue to fine-tune iteration.
                  <div className="bg-white/5 p-4 rounded-lg mt-2">
                    <pre className="text-sm text-white/80 font-mono">
                      <code>{`for (let i = 0; i < 10; i++) {
  if (i === 3) continue; // Skip 3
  if (i === 7) break;    // Stop at 7
  console.log(i);
}`}</code>
                    </pre>
                  </div>
                </li>
                <li>
                  <strong>Early Return:</strong> Exiting functions early to simplify logic.
                  <div className="bg-white/5 p-4 rounded-lg mt-2">
                    <pre className="text-sm text-white/80 font-mono">
                      <code>{`function processInput(input: string) {
  if (!input) return 'No input';
  if (input.length < 3) return 'Too short';
  return input.toUpperCase();
}`}</code>
                    </pre>
                  </div>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Video Tutorial */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white/5 rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Video Tutorial</h2>
            <div className="aspect-w-16 aspect-h-9 bg-black/50 rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                >
                  <source src="https://files.catbox.moe/9eozyv.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
            </div>
          </motion.section>

          {/* Why It Matters */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Why Control Flow Matters</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <ul className="space-y-4 text-white/70">
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Dynamic Behavior:</strong> Enables programs to respond to different inputs and states.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Efficiency:</strong> Optimizes execution by avoiding unnecessary operations.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Reliability:</strong> Prevents crashes through proper error handling.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Scalability:</strong> Supports complex logic in large applications.</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Next Steps */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Next Steps</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                With a solid grasp of control flow, you're ready to:
              </p>
              <ul className="list-disc list-inside text-white/70 space-y-2">
                <li>Implement complex decision-making logic</li>
                <li>Optimize loops for performance</li>
                <li>Handle errors in real-world applications</li>
                <li>Practice with hands-on coding challenges</li>
              </ul>
              <div className="mt-6 flex justify-center">
                <motion.button
                  onClick={() => window.location.href = 'http://localhost:3000/practice/control-flow'}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span>Start Practice</span>
                </motion.button>
              </div>
            </div>
          </motion.section>
          </div>
      </div>
    </div>
  );
};

export default ControlFlow;